import React, { useMemo, useState } from "react";
import { format } from "date-fns";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import ScrollableWrapper from "../common/ScrollableWrapper";
import InlineBanner from "../common/InlineBanner";
import SidePanel from "../common/SidePanel";
import PnoeDetailSection, {
  PnoeDetailsContainer,
  PnoeField,
} from "./PnoeDetailSection";
import PnoeParticipantSection from "./PnoeParticipantSection";
import PnoeProtocolSection from "./PnoeProtocolSection";
import PnoeMeasurementChart from "./PnoeMeasurementChart";
import PnoeTestHeaderCard from "./PnoeTestHeaderCard";
import PnoeEditPanel, { PnoeEditKind } from "./PnoeEditPanel";
import { buildLifestyleRows, PnoeEditBody } from "./pnoeLifestyle";
import PnoePhasesEditPanel from "./PnoePhasesEditPanel";
import PnoeDetailsSkeleton from "./PnoeDetailsSkeleton";
import { usePnoeMeasurementDetail } from "./usePnoeMeasurementDetail";
import { usePnoeCatalogs } from "./usePnoeMeasurements";
import { usePnoeParameterOptions } from "./usePnoeParameters";
import {
  completePnoeMeasurement,
  exportPnoeVo2Data,
  invalidatePnoeMeasurement,
  updatePnoeMeasurementPhases,
  updatePnoeParticipant,
  updatePnoeProtocol,
} from "../../../../common/network/Api";
import {
  MeasurementActionKind,
  formatDurationSeconds,
  getEditLockReason,
  getMeasurementActionPrompt,
  getMeasurementTypeLabel,
  resolveCompositeMeasurementStatus,
  resolveReportStatus,
} from "../../utils/pnoeMeasurementStatus";
import { getStatusMessageColors } from "../../utils/labPanelStatus";
import { formatHrDevice, formatPnoeDevice } from "../../utils/pnoeDeviceLabel";
import { useAppStore } from "../../../../providers/AppStore";

interface PnoeTestDetailsViewProps {
  measurementId: string;
  /**
   * Show the client badge. Only the cross-client History page needs it — the
   * workspace shells already name the subject in their top bar.
   */
  showClientBadge?: boolean;
  /**
   * The customer's own view of their test. Nothing may be changed from it:
   * no edit pencils, no "Edit phases", no status menu, no data export. The
   * affiliate views (company and client workspace) keep all of them.
   */
  readOnly?: boolean;
}

/**
 * Detail page for one PNOĒ breath test.
 *
 * A header card (status / type / date, client, export) over the measurement
 * chart, then the per-section cards, each a two-column label/value grid. The
 * measurement is fetched by id rather than resolved from the listing, so the URL
 * deep-links.
 *
 * There is no page title or back button: the breadcrumb names the page and
 * carries the link back to the Assessments listing (`useBackNavigation`).
 *
 * All fields are read-only. The only mutations offered are the status
 * transitions (complete / invalidate) and the CSV export — the requirements ask
 * for a single page-wide way to edit fields, and that design isn't settled.
 */
const PnoeTestDetailsView: React.FC<PnoeTestDetailsViewProps> = ({
  measurementId,
  showClientBadge = false,
  readOnly = false,
}) => {
  const theme: Theme = useTheme();
  const catalogs = usePnoeCatalogs();
  const { detail, phases, loading, error, live, reload, applyOperation } =
    usePnoeMeasurementDetail(measurementId);
  // Needed even read-only: these turn stored values into display labels.
  const {
    participant: participantOptions,
    resting: restingOptions,
    details: parameterDetails,
    lifestyle: lifestyleParameters,
  } = usePnoeParameterOptions();

  // The lifestyle questionnaire, reconciled with what the service asks today.
  // Empty on a historical measurement with no snapshot (and before the
  // configuration lands), in which case the Client grid ends at its own
  // fields.
  const lifestyleRows = useMemo(
    () =>
      buildLifestyleRows(
        detail?.participant?.lifestyle_answers,
        lifestyleParameters
      ),
    [detail, lifestyleParameters]
  );

  const {
    ASSetSnackBarMessage,
    ASSetTypeSnackBar,
    ASSetSnackBarActions,
    ASSetOpenSnackBar,
  } = useAppStore();

  const [runningAction, setRunningAction] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Which section's edit panel is open, if any. "phases" is the chart's.
  const [editing, setEditing] = useState<PnoeEditKind | "phases" | null>(null);
  // True from the moment a patch is sent until the refetched measurement
  // lands. Drives the details skeleton — the values on screen are stale in
  // between, and a recalculated report can change several at once — and
  // disables the panel's Save so an in-flight edit cannot be sent twice.
  const [savingEdit, setSavingEdit] = useState(false);

  /**
   * A failed request the user asked for, reported the way the rest of the
   * platform reports one: the alert snackbar, with the service's own
   * explanation when it sends one.
   *
   * `detail` is the breath service's (FastAPI's) field and says why an edit
   * was refused — a status it will not accept edits at, a value out of range —
   * which beats anything guessable from the status code. A validation error
   * sends an array of them rather than a sentence, hence the string test.
   *
   * Load failures deliberately do NOT come through here: the detail hook polls
   * while a test is recording, and a snackbar per failed poll would be noise.
   * Those stay in the inline banner.
   */
  const showApiError = (title: string, e?: any) => {
    const detail = e?.response?.data?.detail ?? e?.response?.data?.message;
    ASSetSnackBarMessage({
      title,
      message:
        typeof detail === "string" && detail ? detail : "Please try again.",
    });
    ASSetTypeSnackBar("alert");
    ASSetOpenSnackBar(true);
  };

  /**
   * Save from an edit panel: prompt first, patch only on Confirm.
   *
   * Every field either panel exposes triggers a report recalculation, so the
   * platform's two-action snackbar asks before anything is sent. The panel
   * stays open behind the prompt so Cancel returns to the draft intact.
   */
  const requestEditSave = (kind: PnoeEditKind, changed: PnoeEditBody) => {
    if (!Object.keys(changed).length) return;

    ASSetSnackBarMessage({
      title: "Recalculate the report?",
      message:
        "The current report is replaced by one calculated from the edited values. Any figure that depends on them changes with it.",
    });
    ASSetTypeSnackBar("warning");
    ASSetSnackBarActions({
      onConfirm: () => saveEdit(kind, changed),
    });
    ASSetOpenSnackBar(true);
  };

  /**
   * Phase saves go through the same prompt: a phase boundary is a report input
   * like any other, so moving one recalculates.
   */
  const requestPhasesSave = (
    changed: Record<string, Record<string, number | null>>
  ) => {
    if (!Object.keys(changed).length) return;
    ASSetSnackBarMessage({
      title: "Recalculate the report?",
      message:
        "The current report is replaced by one calculated from the edited values. Any figure that depends on them changes with it.",
    });
    ASSetTypeSnackBar("warning");
    ASSetSnackBarActions({ onConfirm: () => savePhases(changed) });
    ASSetOpenSnackBar(true);
  };

  const savePhases = async (
    changed: Record<string, Record<string, number | null>>
  ) => {
    try {
      setSavingEdit(true);
      // One atomic PATCH for the whole set. The service validates every entry
      // together and rejects all of them if one is bad, so a refused boundary
      // cannot leave the phases half-moved, and the edit costs a single report
      // calculation rather than one per phase.
      const operation = await updatePnoeMeasurementPhases(
        measurementId,
        changed
      );
      setEditing(null);
      // The response carries the new report_status, so the header moves before
      // the refetch returns — the same as a participant or protocol save.
      if (operation) applyOperation(operation);
      await reload(true);
    } catch (e: any) {
      console.warn("Error saving measurement phases:", e);
      showApiError("Couldn't save these phases", e);
    } finally {
      setSavingEdit(false);
    }
  };

  const saveEdit = async (kind: PnoeEditKind, changed: PnoeEditBody) => {
    try {
      setSavingEdit(true);
      const operation =
        kind === "participant"
          ? await updatePnoeParticipant(measurementId, changed)
          : await updatePnoeProtocol(measurementId, changed);
      // Only now is the draft spent. Closing before the request would throw
      // the user's edits away on a rejection — and the service does reject
      // some of these — leaving them to retype everything.
      setEditing(null);
      // The response carries the new report_status, so the header moves before
      // the refetch returns.
      if (operation) applyOperation(operation);
      await reload(true);
    } catch (e: any) {
      console.warn("Error saving measurement edit:", e);
      showApiError("Couldn't save these changes", e);
    } finally {
      setSavingEdit(false);
    }
  };

  /**
   * Status change from the header select: warn first, transition only on
   * Confirm.
   *
   * Each transition has a consequence the menu cannot show — leaving
   * `in_progress` locks the test against further device data, invalidating
   * cancels the report, restoring generates one — so the same two-action
   * snackbar the edit panels use asks before anything is sent.
   */
  const requestStatusChange = (action: MeasurementActionKind) => {
    const prompt = getMeasurementActionPrompt(
      detail?.measurement.status,
      action
    );
    if (!prompt) return;

    ASSetSnackBarMessage({ title: prompt.title, message: prompt.message });
    ASSetTypeSnackBar("warning");
    ASSetSnackBarActions({
      confirmLabel: prompt.confirmLabel,
      onConfirm: () => runAction(action),
    });
    ASSetOpenSnackBar(true);
  };

  const runAction = async (action: MeasurementActionKind) => {
    try {
      setRunningAction(true);
      const operation =
        action === "complete"
          ? await completePnoeMeasurement(measurementId)
          : await invalidatePnoeMeasurement(measurementId);
      // The response carries the new status/report_status, so the header moves
      // immediately; the refetch afterwards picks up everything else.
      if (operation) applyOperation(operation);
      await reload(true);
    } catch (e: any) {
      console.warn("Error running measurement action:", e);
      showApiError(`Couldn't ${action} this test`, e);
    } finally {
      setRunningAction(false);
    }
  };

  /**
   * "PNOE test - Ribera Jessica - AMR - 2026-12-15 14-37.csv".
   *
   * Built from what the page already has, so the file is recognisable in a
   * downloads folder — the id-based fallback said nothing about whose test it
   * was. Every part is dropped if it is missing rather than leaving an empty
   * gap, so the worst case is "PNOE test.csv" and never a stray separator.
   * The date is `yyyy-MM-dd HH-mm` so a folder sorts chronologically per
   * client; `:` is not legal in a filename on Windows or macOS, hence the
   * hyphen between hours and minutes.
   */
  const exportFilename = () => {
    const parts = ["PNOE test"];
    if (participant?.customer_name) parts.push(participant.customer_name);
    const typeLabel = getMeasurementTypeLabel(
      measurement.measurement_type,
      catalogs
    );
    if (typeLabel) parts.push(typeLabel);
    if (measurement.started_at) {
      const started = new Date(measurement.started_at);
      if (!Number.isNaN(started.getTime()))
        parts.push(format(started, "yyyy-MM-dd HH-mm"));
    }
    // A name reaches the OS verbatim, so strip what a file system reserves
    // (a client name is free text and could hold a slash) and collapse the
    // whitespace it leaves behind.
    return `${parts
      .join("_")
      .replace(/[/\\?%*:|"<>]/g, "")
      .replace(/\s+/g, " ")
      .trim()}.csv`;
  };

  const exportCsv = async () => {
    try {
      setExporting(true);
      const response = await exportPnoeVo2Data(measurementId);
      if (!response) return;
      // The endpoint returns the file body, so build the download here. The
      // service sends no `Content-Disposition` (and it is not CORS-exposed
      // even if it did), so the name is ours to make.
      const filename = exportFilename();
      const url = window.URL.createObjectURL(response.data as Blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.warn("Error exporting measurement data:", e);
      showApiError("Couldn't export this test's data", e);
    } finally {
      setExporting(false);
    }
  };

  if (loading && !detail) {
    return (
      <div style={styles.container}>
        <div style={styles.placeholder(theme)}>Loading test…</div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div style={styles.container}>
        <div style={styles.placeholder(theme)}>
          {error ?? "This test could not be loaded."}
        </div>
      </div>
    );
  }

  const { measurement, participant, protocol } = detail;
  // What the header shows: the measurement status with the report status
  // appended, the same combination the Assessments listing renders.
  const status = resolveCompositeMeasurementStatus(
    measurement.status,
    measurement.report_status,
    catalogs
  );
  const report = resolveReportStatus(measurement.report_status, catalogs);
  /**
   * Nothing is editable until the test is `completed`. The service enforces
   * it — a PATCH on a recording test is refused with "Report inputs can be
   * edited only for a completed measurement" — so the affordances are greyed
   * out rather than left to fail.
   *
   * Disabled, not hidden: the reader should still see that the section is
   * editable, and learn why it isn't yet.
   */
  const editLockedReason = getEditLockReason(measurement.status);

  // The protocol has a second condition, about the test type rather than its
  // status: `updatePnoeProtocol` takes the RMR parameters only, so an AMR
  // test's equipment and protocol level (fixed before it starts) have no PATCH
  // path and get no pencil at all.
  const canEditProtocol = measurement.measurement_type === "rmr";

  return (
    <div style={styles.container}>
      <PnoeTestHeaderCard
        status={measurement.status}
        statusLabel={status?.label ?? ""}
        statusTone={status?.tone ?? "info"}
        onStatusSelect={requestStatusChange}
        statusBusy={runningAction}
        typeLabel={getMeasurementTypeLabel(
          measurement.measurement_type,
          catalogs
        )}
        startedAt={measurement.started_at}
        clientName={showClientBadge ? participant?.customer_name : null}
        onExport={exportCsv}
        exporting={exporting}
        readOnly={readOnly}
      />

      <div style={styles.contentWrapper}>
        <ScrollableWrapper>
          <div style={styles.content}>
            {/* Load failures only — see `showApiError`. Requests the user
                asked for report through the snackbar instead. */}
            {error && (
              <InlineBanner
                text={error}
                backgroundColor={
                  getStatusMessageColors("danger", theme).backgroundColor
                }
                iconColor={getStatusMessageColors("danger", theme).iconColor}
                marginBottom={0}
                fontSize={12}
              />
            )}

            {/* The chart sits directly under the header; its own redesign is
                the next step. */}
            <PnoeMeasurementChart
              measurementId={measurementId}
              phases={phases}
              catalogs={catalogs}
              live={live}
              onEditPhases={
                phases.length && !readOnly
                  ? () => setEditing("phases")
                  : undefined
              }
              editPhasesDisabledReason={editLockedReason}
            />

            {/* One container for all three sections, not three cards. */}
            {savingEdit ? (
              <PnoeDetailsSkeleton />
            ) : (
              <PnoeDetailsContainer>
                {/* Participant details and the lifestyle answers in one
                    grid behind one pencil: the same panel edits both, and
                    one PATCH carries both. */}
                <PnoeParticipantSection
                  participant={participant}
                  options={participantOptions}
                  lifestyleRows={lifestyleRows}
                  onEdit={
                    readOnly ? undefined : () => setEditing("participant")
                  }
                  editDisabled={Boolean(editLockedReason)}
                  editDisabledReason={editLockedReason ?? undefined}
                />

                {/* No pencil: nothing in here is patchable. Status has no field
                    of its own either — the header card carries it. */}
                <PnoeDetailSection
                  title="Measurement details"
                  variant="inline"
                  columns={[
                    [
                      <PnoeField
                        key="type"
                        label="Measurement type"
                        value={getMeasurementTypeLabel(
                          measurement.measurement_type,
                          catalogs
                        )}
                      />,
                      <PnoeField
                        key="pnoe-device"
                        label="PNOĒ device"
                        value={formatPnoeDevice(measurement, catalogs)}
                      />,
                    ],
                    [
                      <PnoeField
                        key="started"
                        label="Started at"
                        value={
                          measurement.started_at
                            ? format(
                                new Date(measurement.started_at),
                                "MMM dd, yyyy · HH:mm"
                              )
                            : ""
                        }
                      />,
                      <PnoeField
                        key="hr-device"
                        label="HR device"
                        value={formatHrDevice(measurement)}
                      />,
                    ],
                    [
                      <PnoeField
                        key="duration"
                        label="Recorded duration"
                        value={formatDurationSeconds(
                          measurement.recorded_duration_seconds
                        )}
                      />,
                      <PnoeField
                        key="report-status"
                        label="Report status"
                        value={report?.label}
                      />,
                    ],
                  ]}
                />

                <PnoeProtocolSection
                  measurementType={measurement.measurement_type}
                  protocol={protocol}
                  options={restingOptions}
                  onEdit={
                    canEditProtocol && !readOnly
                      ? () => setEditing("protocol")
                      : undefined
                  }
                  editDisabled={Boolean(editLockedReason)}
                  editDisabledReason={editLockedReason ?? undefined}
                />
              </PnoeDetailsContainer>
            )}
          </div>
        </ScrollableWrapper>
      </div>

      <SidePanel
        open={editing !== null}
        onClose={() => setEditing(null)}
        showBackdrop
      >
        {editing === "phases" && (
          <PnoePhasesEditPanel
            phases={phases}
            protocol={protocol}
            catalogs={catalogs}
            restingOnly={measurement.measurement_type === "rmr"}
            onClose={() => setEditing(null)}
            onSave={requestPhasesSave}
            saving={savingEdit}
          />
        )}

        {editing && editing !== "phases" && (
          <PnoeEditPanel
            kind={editing}
            participant={participant}
            protocol={protocol}
            measurementType={measurement.measurement_type}
            participantOptions={participantOptions}
            restingOptions={restingOptions}
            parameterDetails={parameterDetails}
            lifestyleParameters={lifestyleParameters}
            onClose={() => setEditing(null)}
            onSave={(changed) => requestEditSave(editing, changed)}
            saving={savingEdit}
          />
        )}
      </SidePanel>
    </div>
  );
};

export default PnoeTestDetailsView;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    // Header card, then the scrolling body.
    gap: "16px",
    height: "100%",
  },
  contentWrapper: {
    display: "flex",
    flexDirection: "column" as "column",
    flex: 1,
    minHeight: 0,
    overflow: "hidden" as "hidden",
    marginRight: "-10px",
  },
  content: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
  placeholder: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    padding: "48px 0",
    textAlign: "center" as "center",
  }),
};
